import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface PerformanceMetricsProps {
  data: any;
  timeRange?: string;
}

export function PerformanceMetrics({ data }: PerformanceMetricsProps) {
  if (!data?.trends?.performance) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        {Array(2).fill(0).map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">No data available</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Quality Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Shot Quality Trend</CardTitle>
            <CardDescription>
              Average shot quality over time (out of 10)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.trends.performance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis domain={[6, 10]} />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [value.toFixed(1), 'Quality Score']}
                />
                <Line 
                  type="monotone" 
                  dataKey="quality" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Accuracy Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Shot Accuracy Trend</CardTitle>
            <CardDescription>
              Percentage of shots hitting target zones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.trends.performance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis domain={[50, 100]} />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [`${value}%`, 'Accuracy']}
                />
                <Area 
                  type="monotone" 
                  dataKey="accuracy" 
                  stroke="hsl(var(--secondary))"
                  fill="hsl(var(--secondary))"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Combined Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Combined Performance Metrics</CardTitle>
          <CardDescription>
            Quality and accuracy trends in a single view
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data.trends.performance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date"
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis yAxisId="quality" domain={[6, 10]} />
              <YAxis yAxisId="accuracy" orientation="right" domain={[50, 100]} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value: number, name: string) => [
                  name === 'quality' ? value.toFixed(1) : `${value}%`,
                  name === 'quality' ? 'Quality Score' : 'Accuracy'
                ]}
              />
              <Legend />
              <Line 
                yAxisId="quality"
                type="monotone" 
                dataKey="quality" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                name="Quality Score"
                dot={{ fill: 'hsl(var(--primary))' }}
              />
              <Line 
                yAxisId="accuracy"
                type="monotone" 
                dataKey="accuracy" 
                stroke="hsl(var(--secondary))" 
                strokeWidth={2}
                name="Accuracy %"
                dot={{ fill: 'hsl(var(--secondary))' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}